import React from 'react';

/**
 * Hand-drawn icon set — a small, consistent stroke library so the site pulls
 * in no icon dependency. Every glyph is on a 24 unit grid with a 1.4 stroke.
 */
const paths = {
  arrowRight: <path d="M4 12h15m0 0-5.5-5.5M19 12l-5.5 5.5" />,
  arrowUpRight: <path d="M7.5 16.5 16.5 7.5m0 0H9m7.5 0V15" />,
  chevronLeft: <path d="M14.5 5.5 8 12l6.5 6.5" />,
  chevronRight: <path d="M9.5 5.5 16 12l-6.5 6.5" />,
  close: <path d="M6 6l12 12M18 6 6 18" />,
  menu: <path d="M3.5 7h17M3.5 12h17M3.5 17h17" />,
  phone: (
    <path d="M6.2 3.5h3l1.5 3.8-2 1.4a11.5 11.5 0 0 0 5.6 5.6l1.4-2 3.8 1.5v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 4.2 5.7a2 2 0 0 1 2-2.2Z" />
  ),
  mail: (
    <>
      <rect x="3" y="5.5" width="18" height="13" rx="2" />
      <path d="m3.8 7 7.1 5.3a2 2 0 0 0 2.2 0L20.2 7" />
    </>
  ),
  pin: (
    <>
      <path d="M12 21c4.2-4.4 6.3-7.7 6.3-10.2A6.3 6.3 0 0 0 5.7 10.8C5.7 13.3 7.8 16.6 12 21Z" />
      <circle cx="12" cy="10.6" r="2.4" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.2V12l3 1.8" />
    </>
  ),
  instagram: (
    <>
      <rect x="3.5" y="3.5" width="17" height="17" rx="5" />
      <circle cx="12" cy="12" r="3.8" />
      <circle cx="17" cy="7" r="0.9" fill="currentColor" stroke="none" />
    </>
  ),
  whatsapp: (
    <path d="M4 20l1.2-4a7.6 7.6 0 1 1 3 2.9L4 20Zm5.6-9.4c.3 1.6 1.9 3.2 3.5 3.5.5.1 1.1-.1 1.4-.5l.4-.6-1.8-.9-.5.6a4.6 4.6 0 0 1-1.6-1.6l.6-.5-.9-1.8-.6.4c-.4.3-.6.9-.5 1.4Z" />
  ),
  play: <path d="M8.5 5.8v12.4l10-6.2-10-6.2Z" />,
  pause: <path d="M9 5.5v13M15 5.5v13" />,
  volume: (
    <>
      <path d="M4.5 9.5h3l4-3.2v11.4l-4-3.2h-3z" />
      <path d="M15 9.6a3.4 3.4 0 0 1 0 4.8" />
    </>
  ),
  volumeOff: (
    <>
      <path d="M4.5 9.5h3l4-3.2v11.4l-4-3.2h-3z" />
      <path d="m15.5 9.8 4 4.4m0-4.4-4 4.4" />
    </>
  ),
  seal: (
    <>
      <path d="m12 3 2.2 1.6 2.7-.2.8 2.6 2.2 1.6-1 2.5 1 2.5-2.2 1.6-.8 2.6-2.7-.2L12 21l-2.2-1.6-2.7.2-.8-2.6L4.1 15.4l1-2.5-1-2.5L6.3 8.8l.8-2.6 2.7.2L12 3Z" />
      <path d="m9.4 12.2 1.9 1.9 3.5-3.9" />
    </>
  ),
  gem: (
    <>
      <path d="M6.5 4h11l3 5-8.5 11L3.5 9l3-5Z" />
      <path d="M3.5 9h17M9 4l-1.5 5L12 20l4.5-11L15 4" />
    </>
  ),
  crown: (
    <>
      <path d="M4 17.5h16M4.5 8l3.4 3L12 5.5l4.1 5.5 3.4-3-1.4 8H5.9L4.5 8Z" />
    </>
  ),
  tag: (
    <>
      <path d="M4 11.4V4.5h6.9l8.6 8.6a1.7 1.7 0 0 1 0 2.4l-4.6 4.6a1.7 1.7 0 0 1-2.4 0L4 11.4Z" />
      <circle cx="8.2" cy="8.2" r="1.3" />
    </>
  ),
  sparkle: (
    <path d="M12 3.5c.5 4.2 1.8 5.5 6 6-4.2.5-5.5 1.8-6 6-.5-4.2-1.8-5.5-6-6 4.2-.5 5.5-1.8 6-6Z" />
  ),
  quote: (
    <path d="M9.5 6.5C6.8 7.8 5.5 10 5.5 13v4.5h5.6V12H8.4c0-1.9.6-3.2 2.1-4.1l-1-1.4Zm8.4 0C15.2 7.8 13.9 10 13.9 13v4.5h5.6V12h-2.7c0-1.9.6-3.2 2.1-4.1l-1-1.4Z" />
  ),
};

export function Icon({ name, size = 20, className, strokeWidth = 1.4, ...rest }) {
  const glyph = paths[name];
  if (!glyph) return null;

  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {glyph}
    </svg>
  );
}

/** Solid-fill variants, for glyphs that read better filled than stroked. */
export function SolidIcon({ name, size = 20, className, ...rest }) {
  const glyph = paths[name];
  if (!glyph) return null;

  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="currentColor"
      stroke="none"
      className={className}
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {glyph}
    </svg>
  );
}

export default Icon;
